/**
 * Electrical Installation Certificate (EIC) - BS 7671 Model Form Schema
 * 
 * Follows the IET / NICEIC / NAPIT model form for Electrical Installation
 * Certificates (EIC) covering new electrical installations.
 */

// Shared option arrays
const SUPPLY_SYSTEMS = ['TN-S', 'TN-C-S', 'TN-C', 'TT', 'IT'];
const INSTALLATION_TYPES = ['New Installation', 'Extending Existing Installation', 'Alteration', 'Full Rewire', 'Partial Rewire'];
const DEVICE_TYPES = ['MCB Type B', 'MCB Type C', 'MCB Type D', 'RCBO Type B', 'RCBO Type C', 'RCBO Type D', 'BS 88-2', 'BS 88-3', 'BS 3036', 'BS 1361'];
const DEVICE_RATINGS = ['6', '10', '16', '20', '25', '32', '40', '50', '63', '80', '100', '125'];
const PHASE_ALLOCATIONS = ['Single Phase', 'Three Phase', 'Phase 1 (L1)', 'Phase 2 (L2)', 'Phase 3 (L3)'];
const RCD_TYPES = ['None', '30mA', '100mA', '300mA', '1000mA', 'RCD Type B', 'RCD Type A'];
const TEST_RESULTS = ['SATISFACTORY', 'UNSATISFACTORY', 'NOT TESTED'];
const INSPECTION_RESULTS = ['SATISFACTORY', 'UNSATISFACTORY', 'N/A', 'LIMITED'];

export default {
  sections: [
    {
      id: 'contractor',
      title: '1. Details of the Contractor (Installer)',
      fields: [
        { id: 'companyName', type: 'text', label: 'Contractor Company Name', required: true },
        { id: 'contractorAddress', type: 'textarea', label: 'Contractor Address', required: true },
        { id: 'contractorEmail', type: 'text', label: 'Email' },
        { id: 'contractorPhone', type: 'tel', label: 'Telephone' },
        { id: 'schemeName', type: 'text', label: 'Competent Persons Scheme (e.g. NICEIC, NAPIT)' },
        { id: 'schemeReference', type: 'text', label: 'Scheme Registration Reference' },
        { id: 'engineerName', type: 'text', label: 'Qualified Supervisor / Engineer Name', required: true },
        { id: 'engineerCardNumber', type: 'text', label: 'Engineer Card / License Number', required: true },
        { id: 'notificationDate', type: 'date', label: 'Date of Notification / Confirmation of Works', required: true }
      ]
    },
    {
      id: 'client',
      title: '2. Details of the Client',
      fields: [
        { id: 'clientName', type: 'text', label: 'Client Name', required: true },
        { id: 'clientAddress', type: 'textarea', label: 'Client Address' },
        { id: 'clientEmail', type: 'text', label: 'Client Email' },
        { id: 'clientPhone', type: 'tel', label: 'Client Telephone' }
      ]
    },
    {
      id: 'installation',
      title: '3. Details of the Installation',
      fields: [
        { id: 'installationAddress', type: 'textarea', label: 'Installation Address', required: true },
        { id: 'installationDescription', type: 'textarea', label: 'Description of Installation' },
        { id: 'extentLimitation', type: 'textarea', label: 'Extent & Limitation of Work' },
        { id: 'installationType', type: 'select', label: 'Type of Installation', options: INSTALLATION_TYPES, required: true },
        { id: 'mainSupply', type: 'select', label: 'Main Supply Type', options: SUPPLY_SYSTEMS },
        { id: 'mainProtectiveDevice', type: 'select', label: 'Main Protective Device', options: DEVICE_TYPES },
        { id: 'mainIsolator', type: 'text', label: 'Main Isolator / Switch Fuse' }
      ]
    },
    {
      id: 'supplyCharacteristics',
      title: '4. Supply Characteristics & Particulars of Installation at Origin',
      fields: [
        { id: 'nominalVoltage', type: 'number', label: 'Nominal Voltage (U0)', placeholder: '230' },
        { id: 'externalZe', type: 'number', label: 'External Earth Loop Impedance (Ze)', step: 0.01, placeholder: '0.35' },
        { id: 'earthingType', type: 'select', label: 'Earthing Protection Type', options: SUPPLY_SYSTEMS },
        { id: 'psc', type: 'number', label: 'Prospective Short Circuit Current (PSC)' },
        { id: 'mainFuseSize', type: 'text', label: 'Main Fuse / Service Head Rating' },
        { id: 'mainBonding', type: 'text', label: 'Main Equipotential Bonding' },
        { id: 'supplyCable', type: 'text', label: 'Main Supply Cable Details' }
      ]
    },
    {
      id: 'circuits',
      title: '5. Schedule of Test Results - Circuit Details',
      repeatable: true,
      fields: [
        { id: 'circuitNumber', type: 'text', label: 'Circuit Number' },
        { id: 'dbName', type: 'text', label: 'Distribution Board' },
        { id: 'circuitDescription', type: 'text', label: 'Circuit Description' },
        { id: 'location', type: 'text', label: 'Location / Room', required: true },
        { id: 'phase', type: 'select', label: 'Phase Allocation', options: PHASE_ALLOCATIONS, required: true },
        { id: 'protectiveDevice', type: 'select', label: 'Protective Device Type', options: DEVICE_TYPES, required: true },
        { id: 'deviceRating', type: 'select', label: 'Device Rating', options: DEVICE_RATINGS },
        { id: 'maxZs', type: 'number', label: 'Max Permitted Zs (BS 7671 Table 41.2)', step: 0.01 },
        { id: 'measuredZs', type: 'number', label: 'Measured Zs (Ω)', step: 0.01 },
        { id: 'rcdType', type: 'select', label: 'RCD Type', options: RCD_TYPES },
        { id: 'rcdTripTime1x', type: 'number', label: 'RCD Trip Time 1Δn (ms)' },
        { id: 'rcdTripTime5x', type: 'number', label: 'RCD Trip Time 5Δn (ms)' },
        { id: 'insulationResistanceLL', type: 'number', label: 'Insulation Resistance L-L (MΩ)' },
        { id: 'insulationResistanceLE', type: 'number', label: 'Insulation Resistance L-E (MΩ)' },
        { id: 'insulationResistanceNE', type: 'number', label: 'Insulation Resistance N-E (MΩ)' },
        { id: 'testResult', type: 'select', label: 'Result', options: TEST_RESULTS, required: true }
      ]
    },
    {
      id: 'inspection',
      title: '6. Schedule of Inspections',
      repeatable: true,
      fields: [
        { id: 'inspectionItem', type: 'text', label: 'Inspection Item' },
        { id: 'inspectionResult', type: 'select', label: 'Inspection Result', options: INSPECTION_RESULTS, required: true },
        { id: 'inspectionNotes', type: 'textarea', label: 'Notes / Observations' }
      ]
    },
    {
      id: 'declaration',
      title: '7. Declaration & Certificate',
      fields: [
        { id: 'certificateNumber', type: 'text', label: 'Certificate Number', required: true },
        { id: 'issueDate', type: 'date', label: 'Date of Issue', required: true },
        { id: 'engineerSignature', type: 'signature', label: 'Engineer Signature' },
        { id: 'customerSignature', type: 'signature', label: 'Customer / Client Signature' },
        { id: 'notes', type: 'textarea', label: 'Additional Notes' }
      ]
    }
  ]
};